import { AuthenticatedUserEntity } from "@/api/core/types";
import usePreferredCurrency from "@/hooks/usePreferredCurrency";
import DataQuery, {
  Properties,
} from "@ternary/api-lib/analytics/api/DataQuery";
import { QueryFilter } from "@ternary/api-lib/analytics/types";
import { getDataSourceFilters } from "@ternary/api-lib/analytics/utils/ReportUtils";
import { CurrencyCode } from "@ternary/api-lib/constants/enums";
import {
  FilterStore,
  useFilterStore,
} from "../../../context/FilterStoreProvider";
import useAuthenticatedUser from "../../../hooks/useAuthenticatedUser";

type DataQueryParams = Omit<Properties, "fiscalPeriodMap"> & {
  queryFilters?: QueryFilter[];
};

/**
 * Context values needed to build a DataQuery that would normally come from hooks
 */
export interface DataQueryContext {
  authenticatedUser: AuthenticatedUserEntity;
  filterStore: FilterStore;
  preferredCurrency: CurrencyCode | undefined;
}

/**
 * Hook that returns the tenant ID and query context needed to build a DataQuery
 */
export function useGetDataQueryContext(): {
  tenantID: string;
  queryContext: DataQueryContext;
} {
  const authenticatedUser = useAuthenticatedUser();
  const filterStore = useFilterStore();
  const preferredCurrency = usePreferredCurrency();

  return {
    tenantID: authenticatedUser.tenant.fsDocID,
    queryContext: {
      authenticatedUser,
      filterStore,
      preferredCurrency,
    },
  };
}

/**
 * Non-hook version of useBuildDataQuery that accepts context values directly
 * instead of getting them from hooks. This can be used anywhere in the code,
 * not just at the top level of components.
 *
 * @param params - The query parameters
 * @param context - Values that would normally come from hooks
 * @returns A DataQuery
 */
export function buildDataQuery(
  params: DataQueryParams,
  context: DataQueryContext
) {
  const { authenticatedUser, filterStore, preferredCurrency } = context;

  const currency = params.currency ?? preferredCurrency;

  const fiscalPeriodMap = !params.isFiscalMode
    ? undefined
    : authenticatedUser.tenant.fiscalCalendar?.periods;
  const isFiscalMode = params.isFiscalMode
    ? params.isFiscalMode
    : authenticatedUser.settings.fiscalMode;

  const globalFilters = params?.ignoreGlobalFilters
    ? []
    : getDataSourceFilters(filterStore.queryFilters, params.dataSource);

  // Create a set of measure schema names for filtering
  const measureSet = new Set(
    params.measures?.map((measure) => measure.schemaName) ?? []
  );

  const preAggFiltersFromQueryFilters: QueryFilter[] = [];
  const postAggFiltersFromQueryFilters: QueryFilter[] = [];

  // Helper function to check if a filter belongs to post-agg (measures)
  // This will recursively check through and/or filters
  const isPostAggFilter = (filter: QueryFilter): boolean => {
    // If it has a schemaName directly (BinaryFilter or UnaryFilter)
    if ("schemaName" in filter) {
      return measureSet.has(filter.schemaName);
    }

    // If it's an AndFilter with child filters
    if ("and" in filter) {
      return filter.and.some((childFilter) => isPostAggFilter(childFilter));
    }

    // If it's an OrFilter with child filters
    if ("or" in filter) {
      return filter.or.some((childFilter) => isPostAggFilter(childFilter));
    }

    return false;
  };

  if (params?.queryFilters) {
    // Categorize filters based on our recursive check
    params.queryFilters.forEach((filter) => {
      if (isPostAggFilter(filter)) {
        postAggFiltersFromQueryFilters.push(filter);
      } else {
        preAggFiltersFromQueryFilters.push(filter);
      }
    });
  }

  return new DataQuery({
    ...params,
    currency,
    fiscalPeriodMap,
    isFiscalMode,
    // Combine all pre-aggregation filters
    preAggFilters: [
      ...globalFilters,
      ...preAggFiltersFromQueryFilters,
      ...(params.preAggFilters ?? []),
    ],
    // Combine all post-aggregation filters
    postAggFilters: [
      ...postAggFiltersFromQueryFilters,
      ...(params.postAggFilters ?? []),
    ],
  });
}

// Browser specific wrapper for centralizing some of the logic
// that we always need for doing a Datalligator Query
export function useBuildDataQuery(params: DataQueryParams) {
  const { tenantID, queryContext } = useGetDataQueryContext();

  const query = buildDataQuery(params, queryContext);

  return [tenantID, query] as const;
}

export default useBuildDataQuery;
